/**
 * `<page-instance-jobs>` — one instance's job history, routed at
 * `/instances/:id/jobs`.
 *
 * Implements `SC-F332` / frozen interface `I7` (`06` §1.8, §3.4) as a paged,
 * filtered list: `GET /v1/instances/{id}/jobs` with `?type=` and `?status=` and
 * the product's keyset pagination.
 *
 * Story: `SC-S322`.
 *
 * **This is one tab and not five, and that is the feature.** Provisioning, a
 * publish, a rollback, a restart, a suspend, a deletion, every proxied CLI run, a
 * schema introspection, an audit export and a chain verification are all the same
 * object with the same states, the same progress shape and the same typed error —
 * so "what has happened to this instance" is one list, sorted by time, with a type
 * column. If this screen needed a second progress mechanism to render one of those
 * rows, `I7` would have failed.
 *
 * The relationship to the instance console is deliberate and one-directional: the
 * console's Operations panel shows the most recent eight of exactly this resource
 * under a smaller limit, because an operator glancing at an instance wants "is
 * anything happening right now". This page is the history behind that panel — the
 * "who restarted this instance three weeks ago" question, which is the reason
 * `05` §3.13 keeps a job row for thirty days after it finished.
 *
 * It polls with [watchJob] and nothing else. A running job on the page is followed
 * with the product's one progress helper, and its terminal state reloads the page;
 * there is no interval timer here.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTable,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  JOB_STATUSES,
  JOB_STATUS_LABELS,
  JOB_TYPES,
  JOB_TYPE_LABELS,
  isTerminal,
  jobActorLabel,
  listInstanceJobs,
  watchJob,
  type Job,
  type JobStatus,
  type JobType,
} from "../api/jobs.js";
import { getInstance, type Instance } from "../api/instances.js";
import { hrefFor, navigate } from "../nav.js";

/** The page size. Fifty is the API's own default and the list is keyset-paged, so
 * this is a page boundary rather than a cap on what is readable. */
const PAGE_LIMIT = 50;

const COLUMNS: BnColumn[] = [
  { key: "type", label: "Operation" },
  { key: "status", label: "Status", width: "1px" },
  { key: "progress", label: "Progress" },
  { key: "actor", label: "Started by" },
  { key: "when", label: "When", width: "1px" },
  { key: "job", label: "", width: "1px", align: "right" },
];

@customElement("page-instance-jobs")
export class PageInstanceJobs extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private rows: Job[] = [];
  @state() private next: string | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  @state() private instance: Instance | null = null;

  /** The `?type=` and `?status=` filters. The empty string is "no filter", which
   * the query builder drops rather than sending as an empty parameter. */
  @state() private type: JobType | "" = "";
  @state() private status: JobStatus | "" = "";

  /** True once a filter has been applied, so the empty state can tell "this
   * instance has never run an operation" from "nothing matches this filter" —
   * `SC-S907`'s distinction, and the difference between an onboarding message and
   * a "clear the filter" button. */
  private get filtered(): boolean {
    return this.type !== "" || this.status !== "";
  }

  private watchAbort?: AbortController;
  /** The job currently being followed, so a re-render does not start a second
   * watch on the same id. */
  private watching = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .toolbar {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-md);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      width: 16rem;
    }
    .toolbar-end {
      margin-left: auto;
      display: flex;
      gap: var(--bino-space-sm);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .op {
      font-weight: 500;
      color: var(--bino-text);
      text-decoration: none;
    }
    .op:hover {
      text-decoration: underline;
    }
    .op:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .sub {
      display: block;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .failed {
      display: block;
      margin: var(--bino-space-xs) 0 0;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      max-width: 56ch;
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-size: var(--bino-font-size-sm);
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.watchAbort?.abort();
    this.watchAbort = undefined;
    this.watching = "";
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) {
      this.watchAbort?.abort();
      this.watching = "";
      this.rows = [];
      this.next = null;
      this.instance = null;
      if (this.instanceId) {
        void this.load(null);
        void this.loadInstance();
      }
    }
  }

  /**
   * load fetches one page. `cursor` is the API's own opaque `next`, straight from
   * `bn-table`'s `page-change` — never parsed, because `06` §1.5 makes it a
   * base64url of the sort tuple and a client that read it would break the moment
   * the tuple gained a column.
   */
  private async load(cursor: string | null) {
    this.loading = true;
    try {
      const page = await listInstanceJobs(this.instanceId, {
        limit: PAGE_LIMIT,
        cursor: cursor ?? undefined,
        type: this.type || undefined,
        status: this.status || undefined,
      });
      this.rows = page.items;
      this.next = page.next;
      this.failure = null;
      this.followRunning();
    } catch (err) {
      this.failure = parseError(err);
      this.rows = [];
      this.next = null;
    } finally {
      this.loading = false;
    }
  }

  /** The instance's name for the heading. Best-effort: this page is about the
   * jobs, and an unreadable instance record must not blank the history. */
  private async loadInstance() {
    try {
      this.instance = await getInstance(this.instanceId);
    } catch {
      this.instance = null;
    }
  }

  /**
   * followRunning watches the first live job on the page, if any, and reloads when
   * it reaches a terminal state.
   *
   * One watch and not one per row: at most one *mutating* job runs against an
   * instance at a time — the lock is a partial unique index over `(instance)`, not
   * advisory logic — and the read-only jobs that can run concurrently are seconds
   * long. So a single watch is enough to keep the page honest, and it is the
   * product's one progress helper rather than an interval timer of this page's own.
   */
  private followRunning() {
    const live = this.rows.find((j) => !isTerminal(j.status));
    if (!live) {
      this.watchAbort?.abort();
      this.watching = "";
      return;
    }
    if (this.watching === live.id) return;
    this.watchAbort?.abort();
    this.watching = live.id;
    this.watchAbort = new AbortController();
    const signal = this.watchAbort.signal;
    void watchJob(live.id, {
      signal,
      onUpdate: (job) => {
        // Patch the row in place so the progress column advances without a
        // refetch. The list order cannot change under us: it is newest-first by
        // `(created, id)`, and neither moves.
        this.rows = this.rows.map((row) => (row.id === job.id ? job : row));
      },
    })
      .then(() => {
        if (signal.aborted) return;
        this.watching = "";
        // A terminal job usually means a new one is about to appear (a publish
        // follows a validate), so the page is re-read rather than only patched.
        void this.load(null);
      })
      .catch(() => {
        // The watch broke, not the job. The rows on screen are still the last true
        // observation; the Refresh button is the recovery.
        this.watching = "";
      });
  }

  private onFilterChange() {
    this.rows = [];
    this.next = null;
    this.renderRoot.querySelector<BnTable>("bn-table")?.resetPagination();
    void this.load(null);
  }

  /* ── rendering ───────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /**
   * The two filter pickers.
   *
   * Each carries a first option whose value is the empty string, which is how the
   * filter is *cleared*: `bn-select` reads an empty value as "nothing selected" and
   * shows its placeholder, so the placeholder is the unfiltered label and this option
   * is the way back to it. The query builder drops an empty value rather than sending
   * an empty parameter, and the route's allowlist would refuse one.
   */
  private typeOptions(): BnSelectOption[] {
    return [
      { value: "", label: "Every operation" },
      ...JOB_TYPES.map((t) => ({
        value: t,
        label: JOB_TYPE_LABELS[t] ?? t,
        description: t,
      })),
    ];
  }

  private statusOptions(): BnSelectOption[] {
    return [
      { value: "", label: "Any state" },
      ...JOB_STATUSES.map((s) => ({ value: s, label: JOB_STATUS_LABELS[s] ?? s })),
    ];
  }

  private toRows(): BnRow[] {
    return this.rows.map((j) => ({
      id: j.id,
      type: html`<a class="op" href=${hrefFor("jobs", j.id)}
          >${JOB_TYPE_LABELS[j.type] ?? j.type}</a
        >
        <span class="sub">${j.type}</span>`,
      status: html`<bn-badge variant=${statusVariant(j.status)} ?dot=${!isTerminal(j.status)}>
          ${JOB_STATUS_LABELS[j.status] ?? j.status}
        </bn-badge>
        ${j.error
          ? html`<span class="failed">${j.error.code}: ${j.error.message}</span>`
          : nothing}`,
      progress: j.progress
        ? html`<span>${j.progress.step}</span>
            <span class="sub">step ${j.progress.index} of ${j.progress.total}</span>`
        : html`<span class="sub">${isTerminal(j.status) ? "—" : "not started"}</span>`,
      // The idempotency key is deliberately not here. It belongs to the submission
      // rather than to the actor, and next to a name it reads as though it were the
      // credential that made the change; the job's own page carries it.
      actor: jobActorLabel(j.actor),
      when: html`<span>${when(j.finished ?? j.started ?? j.created)}</span>
        ${j.finished && j.started
          ? html`<span class="sub">took ${elapsed(j.started, j.finished)}</span>`
          : nothing}`,
      job: html`<bn-button size="sm" @click=${() => navigate(hrefFor("jobs", j.id))}>Open</bn-button>`,
    }));
  }

  /** Two empty states, because they are two different facts. */
  private renderEmpty(): TemplateResult {
    if (this.filtered) {
      return html`<bn-empty-state
        icon="⌕"
        heading="No operation matches this filter"
        message="This instance has run operations, but none of this type or in this state. Clearing the filter shows the whole history."
      >
        <bn-button
          slot="actions"
          variant="primary"
          @click=${() => {
            this.type = "";
            this.status = "";
            this.onFilterChange();
          }}
        >
          Clear the filter
        </bn-button>
      </bn-empty-state>`;
    }
    return html`<bn-empty-state
      icon="⟲"
      heading="No operation has run on this instance yet"
      message="Provisioning, publishing, rolling forward, restarts, suspends, deletion, every proxied Sluice command, schema introspection and audit exports all appear here as jobs — one object with one shape, whatever started it."
    >
      <bn-button
        slot="actions"
        variant="primary"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}
      >
        Review the draft
      </bn-button>
    </bn-empty-state>`;
  }

  override render() {
    if (this.failure) {
      return html`
        <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="The job history could not be read">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            ${this.failure.code === "validation_failed"
              ? html`<p>
                  The type and state filters are an allowlist: a value outside
                  <code>06</code> §1.8's closed vocabulary is refused rather than ignored. Clear the
                  filter and try again.
                </p>`
              : nothing}
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load(null)}>Try again</bn-button>
            </div>
          </div>
        </bn-card>
      `;
    }

    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Job history</h1>
          <p class="lede">
            Every operation on ${this.instance?.name ?? "this instance"} that cannot finish inside
            one request is the same object: a provision, a publish, a roll-forward, a restart, a
            proxied Sluice command, an export. This is all of them, newest first, with what happened
            and who asked for it.
          </p>
        </div>
      </header>

      <div class="stack">
        <bn-card flush>
          <div class="toolbar" style="padding: var(--bino-space-md) var(--bino-space-md) 0">
            <bn-field label="Operation">
              <bn-select
                .options=${this.typeOptions()}
                .value=${this.type}
                placeholder="Every operation"
                filterable
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                  this.type = e.detail.value as JobType | "";
                  this.onFilterChange();
                }}
              ></bn-select>
            </bn-field>
            <bn-field label="State">
              <bn-select
                .options=${this.statusOptions()}
                .value=${this.status}
                placeholder="Any state"
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                  this.status = e.detail.value as JobStatus | "";
                  this.onFilterChange();
                }}
              ></bn-select>
            </bn-field>
            <div class="toolbar-end">
              <bn-button @click=${() => void this.load(null)}>Refresh</bn-button>
            </div>
          </div>
          <bn-table
            caption=${`Job history for ${this.instance?.name ?? "this instance"}`}
            .columns=${COLUMNS}
            .rows=${this.toRows()}
            .nextCursor=${this.next}
            paginated
            ?loading=${this.loading}
            @page-change=${(e: CustomEvent<{ cursor: string | null }>) =>
              void this.load(e.detail.cursor)}
          >
            <div slot="empty">${this.renderEmpty()}</div>
          </bn-table>
        </bn-card>

        <bn-panel eyebrow="How long this list goes back">
          <p class="copy">
            A finished job is kept for <strong>30 days</strong>, and a policy test run or an audit
            chain verification for <strong>90</strong> — those two are evidence, and a compliance
            reviewer asks about them long after the run. Older operations are pruned, but what they
            <em>did</em> is not: a publish leaves a numbered generation with its attribution and its
            failure reason, and a lifecycle action leaves an entry on the instance's timeline.
          </p>
          <p class="copy">
            Exactly one mutating operation runs against an instance at a time, so a second publish
            or restart submitted while one is in flight is refused with
            <code>instance_busy</code> naming the job that holds the lock. The read-only commands —
            validate, explain, test, introspection — are accepted against a busy instance, because
            they mutate nothing.
          </p>
        </bn-panel>
      </div>
    `;
  }
}

/** Job status → badge tone. `cancelled` is neutral: a cancelled job is an outcome
 * somebody chose, not a failure. */
function statusVariant(status: JobStatus | string): BnBadgeVariant {
  switch (status) {
    case "succeeded":
      return "success";
    case "failed":
    case "timed_out":
      return "error";
    case "running":
      return "info";
    default:
      return "neutral";
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** elapsed renders how long a finished job took. */
function elapsed(from: string, to: string): string {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "—";
  const ms = b - a;
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s - m * 60)} s`;
}

declare global {
  interface HTMLElementTagNameMap {
    "page-instance-jobs": PageInstanceJobs;
  }
}
